import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Music } from 'lucide-react';
import { fileUrl } from '@/lib/api';

export default function AudioPlayer({ playlist = [], autoplay = false, loop = true, accent }) {
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(autoplay); // browsers require muted autoplay
  const [open, setOpen] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const tracks = useMemo(() => (playlist || []).filter((t) => t && t.media_id), [playlist]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (!autoplay || tracks.length === 0 || !audioRef.current) return;
    const a = audioRef.current;
    a.muted = true; setMuted(true);
    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setPlaying(true)).catch(() => { setPlaying(false); setAutoplayBlocked(true); });
    }
  }, [autoplay, tracks.length]);

  if (tracks.length === 0) return null;
  const cur = tracks[idx] || tracks[0];
  const next = () => setIdx((i) => (i + 1) % tracks.length);
  const prev = () => setIdx((i) => (i - 1 + tracks.length) % tracks.length);
  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); setAutoplayBlocked(false); }
    else { a.pause(); setPlaying(false); }
  };

  const accentStyle = accent ? { color: accent, borderColor: `${accent}66` } : {};

  return (
    <div className="fixed bottom-24 right-4 z-40 md:bottom-6" data-testid="profile-audio-player">
      <audio ref={audioRef} src={fileUrl(cur.media_id)} onEnded={next} loop={loop && tracks.length === 1} preload="metadata" />
      {open ? (
        <div className="flex items-center gap-2 rounded-2xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))]/85 px-3 py-2 shadow-[var(--shadow-elev-2)] backdrop-blur">
          <button onClick={() => setOpen(false)} className="tap-target rounded-full p-1 text-muted-foreground hover:text-foreground" aria-label="minimize" data-testid="audio-minimize"><Music className="h-4 w-4" style={accentStyle} /></button>
          <div className="min-w-0 max-w-[160px]">
            <div className="truncate text-xs font-medium" data-testid="audio-track-title">{cur.title || `Track ${idx + 1}`}</div>
            {cur.artist && <div className="truncate font-mono text-[11px] text-muted-foreground" data-testid="audio-track-artist">{cur.artist}</div>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={prev} className="tap-target rounded-full p-1 text-muted-foreground hover:text-foreground" aria-label="previous" data-testid="audio-prev"><SkipBack className="h-4 w-4" /></button>
            <button onClick={toggle} className="tap-target rounded-full p-2 hover:opacity-90" style={{ backgroundColor: accent || 'hsl(var(--feed-accent))', color: '#0b0c10' }} aria-label={playing ? 'pause' : 'play'} data-testid="audio-play-pause">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button onClick={next} className="tap-target rounded-full p-1 text-muted-foreground hover:text-foreground" aria-label="next" data-testid="audio-next"><SkipForward className="h-4 w-4" /></button>
            <button onClick={() => setMuted((m) => !m)} className="tap-target rounded-full p-1 text-muted-foreground hover:text-foreground" aria-label={muted ? 'unmute' : 'mute'} data-testid="audio-mute-toggle">
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
          {autoplayBlocked && <span className="hidden md:inline rounded-full border border-[hsl(var(--app-border))] px-2 py-0.5 font-mono text-[10px] text-muted-foreground">Tap to play</span>}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="grid h-12 w-12 place-items-center rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))]/85 shadow-[var(--shadow-elev-2)] backdrop-blur" aria-label="open music player" data-testid="audio-open">
          <Music className="h-5 w-5" style={accentStyle} />
        </button>
      )}
    </div>
  );
}
